/**
 * React Query hooks + types for the generic report-email subscriptions surface.
 *
 *   useReportSubscriptions()        GET    /v1/reports/subscriptions
 *   useCreateReportSubscription()   POST   /v1/reports/{report_type}/subscriptions
 *   useDeleteReportSubscription()   DELETE /v1/reports/subscriptions/{id}
 *
 * This replaces the single-boolean model the OMNI control still uses
 * (`useOmniSubscription`, PUT /v1/retail/omni/subscription). Two differences
 * matter:
 *
 * 1. A person may hold MANY subscriptions to the same report, each carrying its
 *    own saved `filters` object. So creating is a POST that always adds a row,
 *    never an upsert, and removing is a DELETE by id rather than a status flip.
 * 2. `filters` is the grid filter state captured at subscribe time. The emailed
 *    sheet contains only the rows matching it, so "Lolli only" and "everything"
 *    are two separate subscriptions rather than one toggle.
 *
 * The api returns `filters_description`, a plain-language rendering of the saved
 * filters, so the Settings list never has to know a given report's filter
 * vocabulary. `filtersLabel` below is only the fallback for an empty string.
 *
 * Resilience: `retry: false` plus the tolerant `normalizeSubscription` read mean
 * a 404 / 503 / shape mismatch surfaces as `available === false` rather than a
 * crash, so every consumer simply hides. The api side ships in parallel.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost } from './api';
import { API_BASE } from './apiBase';

export const REPORTS_API_BASE = `${API_BASE}/v1/reports`;
export const REPORT_SUBSCRIPTIONS_QUERY_KEY = ['report-subscriptions'] as const;

/** Report types that can be subscribed to from a Cockpit page. */
export const INVENTORY_SNAPSHOT_REPORT_TYPE = 'inventory_snapshot_daily';
/**
 * Stores > Item Sales. The saved filters carry store / taxonomy / SKU / item-type
 * choices only: the emailed sheet always covers the most recent complete
 * business day, which the send re-resolves every morning, so no date range is
 * ever frozen into the subscription.
 */
export const ITEM_SALES_REPORT_TYPE = 'item_sales_daily';
/**
 * Reports > Unposted Activity. The saved filters carry store, transaction type, and
 * the zero-or-negative-quantity switch. The emailed workbook is always the report
 * as it stands at send time, so there is no date to freeze.
 */
export const UNPOSTED_REPORT_TYPE = 'unposted_daily';
/**
 * Reports > Loyalty. The saved filters carry store and the comparison choice. The
 * emailed workbook always covers the week to date as of the morning it is sent (the whole of
 * last week on a Sunday), so no week is ever frozen into the subscription.
 */
export const LOYALTY_REPORT_TYPE = 'loyalty_daily';
/**
 * Reports > Store Analytics. The saved filters carry the stores, the column grain and the comparison
 * choice. The emailed workbook always covers the standard windows as of the morning it is sent (20
 * fiscal weeks of returns plus the week to date, last 25 days of discounts, week, month, quarter and year to date of
 * bulk penetration), so no date is ever frozen into the subscription.
 */
export const STORE_ANALYTICS_REPORT_TYPE = 'store_analytics_daily';
/**
 * Reports > On Order. The saved filters carry the stores, vendors, PO statuses, the item search and the
 * past-cancel-date switch. The emailed workbook is always every matching line as the open purchase orders
 * stand at send time, so there is no date to freeze.
 */
export const ON_ORDER_REPORT_TYPE = 'on_order_daily';

export type ReportSubscription = {
  id: number;
  report_type: string;
  /** Human label for the report, supplied by the api's report registry. */
  report_label: string;
  cadence: 'daily';
  status: 'active' | 'unsubscribed';
  /** The grid filter state captured when this subscription was created. */
  filters: Record<string, unknown> | null;
  /** Plain-language rendering of `filters`, built by the api. May be ''. */
  filters_description: string;
  created_at: string;
};

/**
 * Drops keys the api should not receive: `undefined`, `null`, and the empty
 * string. Booleans and zero are KEPT, because `false` is a meaningful filter
 * value (`in_stock: false` means "include rows with no quantity") and dropping
 * it would silently widen the subscription to the server-side default.
 */
export function compactFilters(input: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = v;
  }
  return out;
}

/**
 * What the Settings list shows for a subscription's filters. Prefers the api's
 * own description; falls back to a fixed phrase when it is blank or the
 * subscription carries no filters at all.
 */
export function filtersLabel(sub: Pick<ReportSubscription, 'filters' | 'filters_description'>): string {
  const desc = sub.filters_description?.trim();
  if (desc) return desc;
  if (!sub.filters || Object.keys(sub.filters).length === 0) return 'All rows, no filters';
  return 'All rows, no filters';
}

/**
 * Reads one raw subscription into the typed shape, defensively. Returns null on
 * any shape mismatch (missing/invalid `id` or `report_type`), so a partial
 * payload drops that row instead of rendering a broken one. `cadence` is
 * normalized to 'daily'; `status` falls back to 'active'.
 */
export function normalizeSubscription(raw: unknown): ReportSubscription | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = typeof r.id === 'number' ? r.id : Number(r.id);
  if (!Number.isFinite(id)) return null;
  if (typeof r.report_type !== 'string' || !r.report_type) return null;
  return {
    id,
    report_type: r.report_type,
    report_label: typeof r.report_label === 'string' && r.report_label ? r.report_label : r.report_type,
    cadence: 'daily',
    status: r.status === 'unsubscribed' ? 'unsubscribed' : 'active',
    filters: r.filters && typeof r.filters === 'object' ? (r.filters as Record<string, unknown>) : null,
    filters_description: typeof r.filters_description === 'string' ? r.filters_description : '',
    created_at: typeof r.created_at === 'string' ? r.created_at : '',
  };
}

/** Reads the list envelope, dropping any row that fails to normalize. */
export function normalizeSubscriptionList(raw: unknown): ReportSubscription[] | null {
  if (!raw || typeof raw !== 'object') return null;
  const list = (raw as Record<string, unknown>).subscriptions;
  if (!Array.isArray(list)) return null;
  return list.map(normalizeSubscription).filter((s): s is ReportSubscription => s !== null);
}

export type UseReportSubscriptionsResult = {
  subscriptions: ReportSubscription[];
  /** True once the GET has resolved to a valid list shape. */
  available: boolean;
  isLoading: boolean;
  error: Error | null;
};

/**
 * Every subscription the signed-in caller holds, across all reports. Backs the
 * Settings > Email reports list.
 */
export function useReportSubscriptions(enabled = true): UseReportSubscriptionsResult {
  const query = useQuery<ReportSubscription[] | null, Error>({
    queryKey: REPORT_SUBSCRIPTIONS_QUERY_KEY,
    queryFn: async () => normalizeSubscriptionList(await apiGet<unknown>(`${REPORTS_API_BASE}/subscriptions`)),
    staleTime: 60_000,
    retry: false,
    enabled,
  });

  return {
    subscriptions: query.data ?? [],
    available: !query.isLoading && !query.error && query.data !== null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

export type CreateSubscriptionInput = {
  reportType: string;
  /** Grid filter state to save. Compacted before it is sent. */
  filters?: Record<string, unknown>;
  cadence?: 'daily';
};

/**
 * Creates a subscription. Always adds a row, so subscribing twice with
 * different filters yields two subscriptions, which is the point.
 */
export function useCreateReportSubscription() {
  const qc = useQueryClient();
  return useMutation<ReportSubscription | null, Error, CreateSubscriptionInput>({
    mutationFn: async ({ reportType, filters, cadence }) => {
      const body: Record<string, unknown> = { cadence: cadence ?? 'daily' };
      if (filters) body.filters = compactFilters(filters);
      return normalizeSubscription(
        await apiPost<unknown>(`${REPORTS_API_BASE}/${encodeURIComponent(reportType)}/subscriptions`, body),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: REPORT_SUBSCRIPTIONS_QUERY_KEY });
    },
  });
}

/** Removes one subscription by id. Hard delete, not a status flip. */
export function useDeleteReportSubscription() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: async (id: number) => {
      await apiDelete<unknown>(`${REPORTS_API_BASE}/subscriptions/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: REPORT_SUBSCRIPTIONS_QUERY_KEY });
    },
  });
}
